import { useEffect } from 'react'
import type { Theme } from '../../shared/preferences'

// Light or dark for the whole app: the .dark class on the page root switches every color role (styles/theme.css).
// public/theme.js does the same before the first paint from what this device last saw; this keeps it in step.
const KEY = 'gatheredfrom:theme'
const DEVICE_DARK = '(prefers-color-scheme: dark)'

export const resolveTheme = (choice: Theme, deviceDark: boolean): 'light' | 'dark' =>
  choice === 'system' ? (deviceDark ? 'dark' : 'light') : choice

export function savedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' || value === 'system' ? value : null
  } catch {
    return null
  }
}

// `remember` is off where the page picks its own look (the white front door), so this device's choice is kept.
export function applyTheme(choice: Theme, remember = true) {
  const shown = resolveTheme(choice, matchMedia(DEVICE_DARK).matches)
  document.documentElement.classList.toggle('dark', shown === 'dark')
  // The browser's own bars, and the phone's status bar, match the page (index.html starts them following the device).
  const background = getComputedStyle(document.documentElement).getPropertyValue('--background').trim()
  if (background) {
    for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
      meta.removeAttribute('media')
      meta.setAttribute('content', background)
    }
  }
  if (!remember) return
  try {
    localStorage.setItem(KEY, choice)
  } catch {
    // Storage can be off (private browsing); the account still has the choice.
  }
  // The server colors the iPhone's status bar from this before the page arrives (worker/statusBar.ts).
  document.cookie = `gatheredfrom-theme=${choice}; Path=/; Max-Age=31536000; SameSite=Lax`
}

// Follows a choice, and the device's setting as it changes when the choice is to match it.
export function useTheme(choice: Theme, remember = true) {
  useEffect(() => {
    applyTheme(choice, remember)
    if (choice !== 'system') return
    const device = matchMedia(DEVICE_DARK)
    const follow = () => applyTheme(choice, remember)
    device.addEventListener('change', follow)
    return () => device.removeEventListener('change', follow)
  }, [choice, remember])
}
